import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  FaSearch, FaDownload, FaSyncAlt, FaMoneyCheckAlt,
  FaCheckCircle, FaTimesCircle, FaEdit, FaPlus, FaTrash,
  FaTruck, FaTruckMoving, FaSpinner, FaExclamationTriangle,
  FaUser, FaSave, FaHistory,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom"; // ← add if using react-router

// ─── CONFIG ──────────────────────────────────────────────────────────────────
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";

// ─── TYPES ───────────────────────────────────────────────────────────────────
type PaymentMode = "online" | "cash" | "bank" | "partial";
type SaveState   = "idle" | "saving" | "saved" | "error";

interface PaymentRecord { amount: number; paymentMode: PaymentMode; date: string; }
interface InvoiceUserPayment {
  userType: string; userName: string; amount: number;
  paid: boolean; paymentRecords: PaymentRecord[];
}
interface InvoiceItem {
  id: number; product: string; hsn: string; uom: string;
  qty: number; rate: number; amount: number;
}
interface Invoice {
  id: string; lrNumber: string; invoiceNumber: string;
  company: string; companyAddress: string; companyGst: string;
  companyPan: string; companyEmail: string; date: string;
  sender: string; senderAddress: string;
  receiver: string; receiverAddress: string;
  broker: string; brokerAddress: string;
  transporter: string; driver: string;
  route: string; origin: string; destination: string; vehicle: string;
  paymentType: string; paymentMode: PaymentMode;
  subtotal: number; gst: number; payment: number;
  items: InvoiceItem[]; profit: number;
  userDistributions: InvoiceUserPayment[];
  roundOff: number; amountInWords: string;
  status: "generated" | "partial" | "paid";
  pdfLink: string; subjects: string;
  saveState?: SaveState;        // ← NEW: per-invoice save state
}

interface RawInvoiceRow {
  id: number;
  "InvoiceNo."?: string;
  sender?: number; company?: number; receiver?: number;
  broker?: number;
  transporter?: number; driver?: number; vehicles?: number;
  paymentPayableConsignor?: number;
  payment?: string;
  source?: string; destination?: string;
  paymentStatus?: string;
  senderAmountStatatus?: string;
  receiverAmountStatatus?: string;
  transporterAmountStatatus?: string;
  ItemsDescription?: string;
  EWAYBILLNO?: string; subjects?: string; link?: string;
  created_at?: string;
}

interface ResolvedUser {
  id: number; name: string; email: string; address: string;
  companyName: string; gst: string; pan: string; phone: string; role: string;
}

// ─── USER RESOLVER ────────────────────────────────────────────────────────────
const userCache = new Map<number, ResolvedUser>();

function normaliseUser(raw: Record<string, unknown>, id: number): ResolvedUser {
  const pick = (...keys: string[]): string => {
    for (const k of keys) {
      const v = raw[k];
      if (v && typeof v === "string" && v.trim()) return v.trim();
    }
    return "";
  };
  return {
    id,
    name:        pick("name","full_name","fullName","username","user_name","firstName","first_name","displayName","display_name"),
    email:       pick("email","email_address","emailAddress"),
    address:     pick("address","full_address","location","city"),
    companyName: pick("company_name","companyName","company","firm_name","firmName","business_name","businessName","organization"),
    gst:         pick("gst","gst_number","gstNumber","gstin","GSTIN"),
    pan:         pick("pan","pan_number","panNumber","pancard","PAN"),
    phone:       pick("phone","mobile","contact","phone_number","phoneNumber"),
    role:        pick("role","user_role","userRole","type"),
  };
}

async function fetchUser(id: number): Promise<ResolvedUser> {
  if (userCache.has(id)) return userCache.get(id)!;
  try {
    const res = await fetch(`${API_BASE}/auth/user/${id}`, { credentials: "include" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const raw: Record<string, unknown> = await res.json();
    const user = normaliseUser(raw, id);
    userCache.set(id, user);
    return user;
  } catch {
    const stub: ResolvedUser = { id, name: `#${id}`, email:"", address:"", companyName:"", gst:"", pan:"", phone:"", role:"" };
    userCache.set(id, stub);
    return stub;
  }
}

async function fetchDriverName(driverId: number): Promise<string> {
  if (!driverId) return "—";
  try {
    const res = await fetch(`${API_BASE}/auth/drivers/${driverId}`, { credentials: "include" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    if (typeof data === "object") {
      for (const key of ["driverName", "name", "full_name"]) {
        if (typeof data[key] === "string" && data[key].trim()) return data[key].trim();
      }
    }
    return `#${driverId}`;
  } catch {
    return `#${driverId}`;
  }
}

async function resolveAllUsers(rows: RawInvoiceRow[]): Promise<{ users: Map<number, ResolvedUser>, driverNames: Map<number, string> }> {
  const ids = new Set<number>();
  const driverIds = new Set<number>();
  for (const row of rows) {
    for (const f of [row.sender, row.company, row.receiver, row.broker, row.transporter, row.paymentPayableConsignor]) {
      if (typeof f === "number") ids.add(f);
    }
    if (typeof row.driver === "number") driverIds.add(row.driver);
  }
  const [usersArray, driverNameArray] = await Promise.all([
    Promise.all([...ids].map(fetchUser)),
    Promise.all([...driverIds].map(fetchDriverName)),
  ]);
  const users = new Map<number, ResolvedUser>();
  usersArray.forEach((u) => users.set(u.id, u));
  const driverNames = new Map<number, string>();
  [...driverIds].forEach((driverId, i) => driverNames.set(driverId, driverNameArray[i]));
  return { users, driverNames };
}

// ─── HELPERS ─────────────────────────────────────────────────────────────────
function parseIndianCurrency(value: unknown): number {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const n = Number(value.replace(/[₹,\s]/g, ""));
    return isNaN(n) ? 0 : n;
  }
  return 0;
}

function displayName(u: ResolvedUser | undefined, fallbackId: number | undefined): string {
  if (!u) return fallbackId ? `#${fallbackId}` : "—";
  return u.companyName || u.name || `#${u.id}`;
}

function normalizePaymentMode(raw: string): PaymentMode {
  const l = raw.toLowerCase();
  if (l.includes("online")) return "online";
  if (l.includes("cash"))   return "cash";
  if (l.includes("partial")) return "partial";
  return "bank";
}

function deriveStatus(paymentStatus: string | undefined): Invoice["status"] {
  const s = (paymentStatus ?? "").toLowerCase();
  if (s === "paid" || s === "completed") return "paid";
  if (s === "partial") return "partial";
  return "generated";
}

function parseItemIds(raw: string | undefined): number[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.filter((x) => typeof x === "number");
  } catch {}
  return [];
}

// ─── MAPPER ──────────────────────────────────────────────────────────────────
function mapRawToInvoice(
  raw: RawInvoiceRow,
  users: Map<number, ResolvedUser>,
  driverNames?: Map<number, string>
): Invoice {
  const company     = users.get(raw.company     ?? -1);
  const sender      = users.get(raw.sender      ?? -1);
  const receiver    = users.get(raw.receiver    ?? -1);
  const broker      = users.get(raw.broker      ?? -1);
  const transporter = users.get(raw.transporter ?? -1);

  const total         = parseIndianCurrency(raw.payment);
  const invoiceNumber = raw["InvoiceNo."] ?? `INV-${raw.id}`;
  const origin        = raw.source?.trim()      || "—";
  const destination   = raw.destination?.trim() || "—";
  const pdfLink       = raw.link
    ? raw.link.startsWith("http") ? raw.link : `${API_BASE}${raw.link}`
    : "";

  let driverValue: string;
  if (typeof raw.driver === "number" && driverNames) {
    driverValue = driverNames.get(raw.driver) || `#${raw.driver}`;
  } else if (typeof raw.driver === "number") {
    driverValue = `#${raw.driver}`;
  } else {
    driverValue = "—";
  }

  return {
    id: String(raw.id),
    lrNumber: invoiceNumber, invoiceNumber,
    company:        displayName(company, raw.company),
    companyAddress: company?.address  || "",
    companyGst:     company?.gst      || "",
    companyPan:     company?.pan      || "",
    companyEmail:   company?.email    || "",
    date:           raw.created_at    ?? "",
    sender:         displayName(sender, raw.sender),
    senderAddress:  sender?.address   || "",
    receiver:       displayName(receiver, raw.receiver),
    receiverAddress:receiver?.address || "",
    broker:         displayName(broker, raw.broker),
    brokerAddress:  broker?.address   || "",
    transporter:    displayName(transporter, raw.transporter),
    driver:         driverValue,
    route:          origin !== "—" && destination !== "—" ? `${origin} → ${destination}` : "—",
    origin, destination,
    vehicle:        raw.vehicles ? `#${raw.vehicles}` : "—",
    paymentType:    raw.paymentStatus ?? "—",
    paymentMode:    normalizePaymentMode(raw.paymentStatus ?? ""),
    subtotal: total, gst: 0, payment: total, profit: 0,
    roundOff: 0, amountInWords: "",
    status:         deriveStatus(raw.paymentStatus),
    userDistributions: [],
    pdfLink,
    subjects: raw.subjects ?? "",
    saveState: "idle",
    items: parseItemIds(raw.ItemsDescription).map((itemId) => ({
      id: itemId, product: `Item #${itemId}`,
      hsn: "—", uom: "—", qty: 0, rate: 0, amount: 0,
    })),
  };
}

// ─── SETTLEMENT DB API ────────────────────────────────────────────────────────
async function loadSettlementFromDB(invoiceId: string): Promise<InvoiceUserPayment[] | null> {
  try {
    const res = await fetch(`${API_BASE}/settlment/${invoiceId}`, { credentials: "include" });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data || !Array.isArray(data.distributions)) return null;
    return data.distributions.map((d: Record<string, unknown>) => ({
      userType: String(d.user_type || ""),
      userName: String(d.user_name || ""),
      amount:   Number(d.amount   || 0),
      paid:     Boolean(d.paid),
      paymentRecords: Array.isArray(d.paymentRecords)
        ? (d.paymentRecords as Array<Record<string, unknown>>).map((r) => ({
            amount: Number(r.amount || 0),
            paymentMode: String(r.paymentMode || "bank") as PaymentMode,
            date: String(r.date || ""),
          }))
        : [],
    }));
  } catch {
    return null;
  }
}

async function saveSettlementToDB(invoice: Invoice): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/settlment/${invoice.id}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        invoiceNo:     invoice.invoiceNumber,
        totalAmount:   invoice.payment,
        distributions: invoice.userDistributions.map((d) => ({
          userType:      d.userType,
          userName:      d.userName,
          amount:        d.amount,
          paymentRecords: d.paymentRecords,
        })),
      }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

// ─── FETCH PIPELINE ───────────────────────────────────────────────────────────
async function fetchAllInvoices(): Promise<Invoice[]> {
  const res = await fetch(`${API_BASE}/invoiceapi/invoice`, { credentials: "include" });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
  const rows: RawInvoiceRow[] = await res.json();
  if (!Array.isArray(rows)) throw new Error("Unexpected response format");
  const { users, driverNames } = await resolveAllUsers(rows);
  return rows.map((row) => mapRawToInvoice(row, users, driverNames));
}

// ─── UI HELPERS ───────────────────────────────────────────────────────────────
const userTypeLabel: Record<string, string> = {
  sender:"Sender", receiver:"Receiver", broker:"Broker",
  transporter:"Transporter", consignor:"Consignor",
  gst:"GST", tds:"TDS", profit:"Profit", driver:"Driver",
};
const paymentModeLabel = (m: PaymentMode) =>
  ({ online:"Online", bank:"Bank", cash:"Cash", partial:"Partial" }[m]);

function statusColor(s: Invoice["status"]) {
  if (s === "paid")    return "text-green-800 bg-green-100";
  if (s === "partial") return "text-yellow-800 bg-yellow-100";
  return "text-blue-800 bg-blue-100";
}
function sumPaid(u: InvoiceUserPayment) {
  return u.paymentRecords.reduce((a, r) => a + (r.amount || 0), 0);
}
function sumAllocated(dists: InvoiceUserPayment[]) {
  return dists.reduce((a, d) => a + (d.amount || 0), 0);
}
const presetUserTypes = ["sender","receiver","broker","driver","consignor","gst","tds","profit"];

interface InvoiceParticipant { userType: string; userName: string; }

function getInvoiceParticipants(invoice: Invoice): InvoiceParticipant[] {
  const participants: InvoiceParticipant[] = [];
  if (invoice.receiver && invoice.receiver !== "—") participants.push({ userType: "receiver", userName: invoice.receiver });
  if (invoice.broker   && invoice.broker   !== "—") participants.push({ userType: "broker",   userName: invoice.broker });
  if (invoice.driver   && invoice.driver   !== "—") participants.push({ userType: "driver",   userName: invoice.driver });
  return participants;
}

// ─── SAVE BUTTON ──────────────────────────────────────────────────────────────
function SaveButton({ state, onClick }: { state: SaveState; onClick: () => void }) {
  if (state === "saving") return (
    <button disabled className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-blue-100 text-blue-600 font-bold border border-blue-200">
      <FaSpinner className="animate-spin" /> Saving…
    </button>
  );
  if (state === "saved") return (
    <button disabled className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-green-100 text-green-700 font-bold border border-green-200">
      <FaCheckCircle /> Saved
    </button>
  );
  if (state === "error") return (
    <button onClick={onClick} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-red-100 text-red-700 font-bold border border-red-300 hover:bg-red-200">
      <FaExclamationTriangle /> Retry Save
    </button>
  );
  return (
    <button onClick={onClick} className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg bg-blue-600 text-white font-bold border border-blue-700 hover:bg-blue-800 transition shadow">
      <FaSave /> Save to DB
    </button>
  );
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
const Settlement: React.FC = () => {
  const navigate = useNavigate(); // remove if not using react-router

  const [invoices, setInvoices]     = useState<Invoice[]>([]);
  const [loading,  setLoading]      = useState(true);
  const [error,    setError]        = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [search,   setSearch]       = useState("");
  const [selectedUserTab, setSelectedUserTab] = useState<Record<string, number>>({});
  const [loadingSettlement, setLoadingSettlement] = useState<Record<string, boolean>>({});

  const [payModal, setPayModal] = useState<{
    invoiceId:string; userIdx:number; amt:string; mode:PaymentMode; date:string;
    full?:boolean; editing?:boolean; editingIdx?:number;
  } | null>(null);

  const [editDist, setEditDist] = useState<{
    invoiceId: string;
    editingIdx: number | null;
    source: "auto" | "manual";
    selectedParticipant: string;
    userType: string;
    userName: string;
    amount: string;
  } | null>(null);

  // debounce auto-save timers per invoice
  const saveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setInvoices(await fetchAllInvoices()); }
    catch (e) { setError(e instanceof Error ? e.message : "Failed to load"); }
    finally   { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // When an invoice is expanded, load its settlement from DB
  useEffect(() => {
    if (!expandedId) return;
    const invoice = invoices.find((i) => i.id === expandedId);
    if (!invoice || invoice.userDistributions.length > 0) return; // already loaded
    setLoadingSettlement((p) => ({ ...p, [expandedId]: true }));
    loadSettlementFromDB(expandedId).then((dists) => {
      if (dists && dists.length > 0) {
        setInvoices((prev) => prev.map((inv) =>
          inv.id === expandedId ? { ...inv, userDistributions: dists } : inv
        ));
      }
      setLoadingSettlement((p) => ({ ...p, [expandedId]: false }));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedId]);

  // ── auto-save with debounce ────────────────────────────────────────────────
  function scheduleAutoSave(invoiceId: string) {
    if (saveTimers.current[invoiceId]) clearTimeout(saveTimers.current[invoiceId]);
    saveTimers.current[invoiceId] = setTimeout(() => {
      triggerSave(invoiceId);
    }, 1500); // 1.5s debounce
  }

  async function triggerSave(invoiceId: string) {
    setInvoices((prev) => prev.map((inv) =>
      inv.id === invoiceId ? { ...inv, saveState: "saving" } : inv
    ));
    const invoice = invoices.find((i) => i.id === invoiceId)
                 || (await new Promise<Invoice | undefined>((resolve) => {
                      setInvoices((prev) => { resolve(prev.find((i) => i.id === invoiceId)); return prev; });
                    }));
    if (!invoice) return;
    const ok = await saveSettlementToDB(invoice);
    setInvoices((prev) => prev.map((inv) =>
      inv.id === invoiceId ? { ...inv, saveState: ok ? "saved" : "error" } : inv
    ));
    // reset back to idle after 3s
    setTimeout(() => {
      setInvoices((prev) => prev.map((inv) =>
        inv.id === invoiceId && inv.saveState === "saved" ? { ...inv, saveState: "idle" } : inv
      ));
    }, 3000);
  }

  // Save using latest invoice state (avoids stale closure)
  async function triggerSaveLatest(invoiceId: string, latest: Invoice) {
    setInvoices((prev) => prev.map((inv) =>
      inv.id === invoiceId ? { ...inv, saveState: "saving" } : inv
    ));
    const ok = await saveSettlementToDB(latest);
    setInvoices((prev) => prev.map((inv) =>
      inv.id === invoiceId ? { ...inv, saveState: ok ? "saved" : "error" } : inv
    ));
    setTimeout(() => {
      setInvoices((prev) => prev.map((inv) =>
        inv.id === invoiceId && inv.saveState === "saved" ? { ...inv, saveState: "idle" } : inv
      ));
    }, 3000);
  }

  const filtered = invoices.filter((inv) =>
    `${inv.lrNumber} ${inv.company} ${inv.origin} ${inv.destination} ${inv.sender} ${inv.receiver} ${inv.broker}`
      .toLowerCase().includes(search.toLowerCase())
  );

  // ── payment handlers ──────────────────────────────────────────────────────
  function openPay(invoiceId:string, userIdx:number, amt:number, mode:PaymentMode, date:string, full=false, editing=false, editingIdx?:number) {
    setPayModal({ invoiceId, userIdx, amt:amt.toString(), mode, date, full, editing, editingIdx });
  }

  function handlePayment(invoiceId:string, userIdx:number, amt:number, mode:PaymentMode, date:string, editingIdx?:number) {
    let updatedInvoice: Invoice | undefined;
    setInvoices((prev) => prev.map((inv) => {
      if (inv.id !== invoiceId) return inv;
      const dists = inv.userDistributions.map((u, i) => {
        if (i !== userIdx) return u;
        let recs = [...u.paymentRecords];
        if (typeof editingIdx === "number") recs[editingIdx] = { amount:amt, paymentMode:mode, date };
        else recs.push({ amount:amt, paymentMode:mode, date });
        recs = recs.filter((r) => r.amount > 0);
        return { ...u, paymentRecords:recs, paid:recs.reduce((a,r)=>a+r.amount,0) >= u.amount };
      });
      const status = dists.every((d)=>d.paid)?"paid":dists.some((d)=>sumPaid(d)>0)?"partial":"generated";
      updatedInvoice = { ...inv, userDistributions:dists, status, saveState: "idle" };
      return updatedInvoice;
    }));
    setPayModal(null);
    // auto-save after state update
    setTimeout(() => {
      if (updatedInvoice) triggerSaveLatest(invoiceId, updatedInvoice);
    }, 100);
  }

  function removePayRec(invoiceId:string, userIdx:number, payIdx:number) {
    let updatedInvoice: Invoice | undefined;
    setInvoices((prev) => prev.map((inv) => {
      if (inv.id !== invoiceId) return inv;
      const dists = inv.userDistributions.map((u,i) => {
        if (i !== userIdx) return u;
        const recs = u.paymentRecords.filter((_,j)=>j!==payIdx);
        return { ...u, paymentRecords:recs, paid:recs.reduce((a,r)=>a+r.amount,0) >= u.amount };
      });
      const status = dists.every((d)=>d.paid)?"paid":dists.some((d)=>sumPaid(d)>0)?"partial":"generated";
      updatedInvoice = { ...inv, userDistributions:dists, status };
      return updatedInvoice;
    }));
    setTimeout(() => {
      if (updatedInvoice) triggerSaveLatest(invoiceId, updatedInvoice);
    }, 100);
  }

  // ── distribution edit ─────────────────────────────────────────────────────
  function openEditDist(invoice: Invoice, idx: number | null, u?: InvoiceUserPayment) {
    if (u) {
      const participants = getInvoiceParticipants(invoice);
      const isAuto = participants.some((p) => p.userType === u.userType && p.userName === u.userName);
      setEditDist({ invoiceId: invoice.id, editingIdx: idx, source: isAuto?"auto":"manual",
        selectedParticipant: isAuto?`${u.userType}::${u.userName}`:"",
        userType: u.userType, userName: u.userName, amount: u.amount?.toString()||"" });
    } else {
      const participants = getInvoiceParticipants(invoice);
      const firstP = participants[0];
      setEditDist({ invoiceId: invoice.id, editingIdx: null, source: "auto",
        selectedParticipant: firstP?`${firstP.userType}::${firstP.userName}`:"",
        userType: firstP?.userType||"", userName: firstP?.userName||"", amount: "" });
    }
  }

  function handleParticipantSelect(key: string, participants: InvoiceParticipant[]) {
    if (key === "__manual__") {
      setEditDist((p) => p ? { ...p, source:"manual", selectedParticipant:"", userType:"", userName:"" } : p);
    } else {
      const found = participants.find((p) => `${p.userType}::${p.userName}` === key);
      if (found) {
        setEditDist((p) => p ? { ...p, source:"auto", selectedParticipant:key, userType:found.userType, userName:found.userName } : p);
      }
    }
  }

  function saveEditDist() {
    if (!editDist) return;
    const { invoiceId, editingIdx, userType, userName, amount } = editDist;
    if (!userType || Number(amount) < 1) return;
    let updatedInvoice: Invoice | undefined;
    setInvoices((prev) => prev.map((inv) => {
      if (inv.id !== invoiceId) return inv;
      const others = inv.userDistributions.filter((_,i)=>i!==editingIdx);
      if (sumAllocated(others) + Number(amount) > inv.payment) return inv;
      let list = [...inv.userDistributions];
      const existingRecs = editingIdx != null ? inv.userDistributions[editingIdx].paymentRecords : [];
      const existingPaid = existingRecs.reduce((a,r)=>a+r.amount,0);
      const entry: InvoiceUserPayment = {
        userType, userName, amount: Number(amount),
        paid: existingPaid >= Number(amount),
        paymentRecords: existingRecs,
      };
      if (editingIdx != null) list[editingIdx] = entry;
      else list.push(entry);
      const status = list.every((d)=>d.paid)?"paid":list.some((d)=>sumPaid(d)>0)?"partial":"generated";
      updatedInvoice = { ...inv, userDistributions:list, status };
      return updatedInvoice;
    }));
    setEditDist(null);
    setTimeout(() => {
      if (updatedInvoice) triggerSaveLatest(invoiceId, updatedInvoice);
    }, 100);
  }

  function removeDist(invoiceId:string, idx:number) {
    let updatedInvoice: Invoice | undefined;
    setInvoices((prev) => prev.map((inv) => {
      if (inv.id !== invoiceId) return inv;
      const list = inv.userDistributions.filter((_,i)=>i!==idx);
      setSelectedUserTab((t)=>({ ...t, [invoiceId]:Math.max(0,Math.min(t[invoiceId]??0,list.length-1)) }));
      const status = list.every((d)=>d.paid)?"paid":list.some((d)=>sumPaid(d)>0)?"partial":"generated";
      updatedInvoice = { ...inv, userDistributions:list, status };
      return updatedInvoice;
    }));
    setTimeout(() => {
      if (updatedInvoice) triggerSaveLatest(invoiceId, updatedInvoice);
    }, 100);
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#cfdbff] via-[#edf1fe] to-[#edf1fe] flex flex-col items-stretch p-[4px] md:pt-0 md:pb-0 md:px-0">
      <div className="w-full py-8 px-1 md:px-4 max-w-full">

        {/* Header */}
        <div className="flex flex-col gap-3 md:flex-row md:justify-between md:items-end mb-8 w-full">
          <div>
            <h1 className="text-4xl lg:text-5xl font-black tracking-tight drop-shadow-lg">
              <span className="text-black">Settlement Center</span>
            </h1>
            <div className="text-base md:text-lg font-semibold text-black drop-shadow mt-1">All Invoice Payouts & Settlement</div>
          </div>
          <div className="flex items-center gap-2 mt-3 md:mt-0">
            {/* ← NEW: Records Page button */}
            <button
              onClick={() => navigate("/settlement/records")}
              className="rounded-xl px-4 py-2 bg-white text-blue-700 text-base font-bold border-2 border-blue-300 shadow hover:bg-blue-600 hover:text-white transition whitespace-nowrap flex items-center gap-2">
              <FaHistory /> View Records
            </button>
            <div className="relative w-full max-w-xs">
              <FaSearch className="absolute top-1/2 left-4 -translate-y-1/2 text-blue-600 text-lg" />
              <input type="text" placeholder="Search LR / Company / Route / Sender / Broker…"
                className="pl-10 pr-3 py-2 rounded-xl border border-blue-600 font-bold text-black bg-white/95 shadow focus:bg-blue-50 transition w-full"
                value={search} onChange={(e)=>setSearch(e.target.value)} />
            </div>
            <button className="rounded-xl px-4 py-2 bg-blue-600 text-white text-base font-bold border-2 border-blue-600 shadow hover:bg-black hover:text-blue-200 transition whitespace-nowrap disabled:opacity-60"
              onClick={load} disabled={loading}>
              {loading ? <FaSpinner className="inline mr-1 animate-spin" /> : <FaSyncAlt className="inline mr-1" />}
              Refresh
            </button>
          </div>
        </div>

        {loading && (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <FaSpinner className="text-blue-600 text-4xl animate-spin" />
            <span className="text-blue-800 font-bold text-lg">Loading invoices…</span>
          </div>
        )}
        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <FaExclamationTriangle className="text-red-500 text-4xl" />
            <div className="text-red-700 font-bold text-lg text-center">{error}</div>
            <button onClick={load} className="mt-2 px-6 py-2 bg-blue-600 text-white rounded-xl font-bold hover:bg-black transition">Retry</button>
          </div>
        )}

        {!loading && !error && (
          <div className="flex flex-col gap-6 pb-20 w-full max-w-full">
            {filtered.length === 0 && (
              <div className="text-center bg-blue-50 py-8 rounded-xl text-blue-700 font-bold text-lg mt-10 shadow">No invoices found.</div>
            )}

            {filtered.map((invoice) => {
              const expanded       = expandedId === invoice.id;
              const activeIdx      = selectedUserTab[invoice.id] ?? 0;
              const activeUser     = invoice.userDistributions[activeIdx];
              const totalAllocated = sumAllocated(invoice.userDistributions);
              const remaining      = invoice.payment - totalAllocated;
              const participants   = getInvoiceParticipants(invoice);

              return (
                <div key={invoice.id} className={`relative w-full bg-white shadow-lg border-t-4 ${
                  invoice.status==="paid"?"border-green-600":invoice.status==="partial"?"border-blue-400":"border-blue-600"
                } rounded-2xl transition-all hover:shadow-2xl hover:scale-[1.01]`}>

                  {/* Card header */}
                  <button type="button" aria-expanded={expanded}
                    onClick={()=>setExpandedId(expanded?null:invoice.id)}
                    className={`w-full relative rounded-2xl overflow-hidden bg-white border-2 ${invoice.status==="paid"?"border-green-300":"border-blue-200"} shadow-md hover:shadow-lg p-0`}
                    style={{minHeight:100}}>
                    <div className={`w-full h-2 ${invoice.status==="paid"?"bg-green-200":"bg-blue-100"}`} />
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-0">
                      <div className="flex h-[60px] w-[60px] sm:h-[84px] sm:w-[84px] items-center justify-center pl-3 bg-white border-r border-b sm:border-b-0 border-blue-50">
                        <span className="p-2 rounded-lg bg-blue-100/60 border border-blue-200 shadow">
                          <FaTruckMoving className="text-blue-500 text-2xl sm:text-3xl" />
                        </span>
                      </div>
                      <div className="flex-1 flex flex-col justify-center pl-3 sm:pl-6 pr-3 py-3 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-extrabold text-xl sm:text-2xl text-blue-900 truncate capitalize">{invoice.company}</span>
                          {invoice.status==="paid"    && <FaCheckCircle   className="text-green-500 text-lg" />}
                          {invoice.status==="partial" && <FaMoneyCheckAlt className="text-blue-400 text-lg" />}
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[14px] text-blue-900/90 font-medium items-center">
                          <div className="flex gap-2 items-center">
                            <span className="rounded px-2 py-0.5 bg-blue-50 border text-xs font-mono text-blue-800">LR</span>
                            <span className="font-bold text-sky-700">{invoice.lrNumber}</span>
                          </div>
                          {invoice.date && (
                            <span className="text-sm">{new Date(invoice.date).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"2-digit"})}</span>
                          )}
                          {invoice.origin!=="—" && (
                            <div className="flex gap-1 items-center">
                              <FaTruck className="text-blue-300" />
                              <span className="font-semibold text-blue-600">{invoice.origin}</span>
                              <span className="mx-1">→</span>
                              <span className="font-semibold text-blue-600">{invoice.destination}</span>
                            </div>
                          )}
                          <span className="font-extrabold text-green-700 text-base">₹{invoice.payment.toLocaleString("en-IN")}</span>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-3">
                          {[
                            {label:"Sender",      val:invoice.sender},
                            {label:"Receiver",    val:invoice.receiver},
                            {label:"Broker",      val:invoice.broker},
                            {label:"Transporter", val:invoice.transporter},
                            {label:"Driver",      val:invoice.driver},
                          ].filter((x)=>x.val&&x.val!=="—").map((x)=>(
                            <span key={x.label} className="border border-blue-100 rounded px-2 py-0.5 bg-white text-xs font-semibold text-blue-700">
                              {x.label}: <span className="font-bold">{x.val}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex flex-row sm:flex-col items-center justify-between sm:justify-center px-4 py-1 sm:pr-6 gap-1 min-w-[105px]">
                        <span className={`rounded-lg px-3 sm:px-5 py-1 font-extrabold text-xs uppercase tracking-widest border-2 border-blue-100 ${statusColor(invoice.status)}`}>
                          {invoice.status}
                        </span>
                        <span className={`transition-transform duration-200 text-blue-400 text-xl sm:mt-3 rounded-full w-8 h-8 flex items-center justify-center border border-blue-100 bg-white shadow-sm ${expanded?"rotate-180":""}`}>▼</span>
                      </div>
                    </div>
                    <div className="absolute bottom-0 left-7 right-7 h-[2px] bg-blue-50" />
                  </button>

                  {/* Expanded */}
                  {expanded && (
                    <div className="px-2 sm:px-4 lg:px-8 pb-6 pt-2 w-full">

                      {/* Save bar */}
                      <div className="flex items-center justify-between py-2 mb-2 border-b border-blue-100">
                        <span className="text-xs text-blue-500 font-semibold">Changes auto-save when you add payments or distributions.</span>
                        <div className="flex items-center gap-2">
                          {loadingSettlement[invoice.id] && (
                            <span className="text-xs text-blue-400 flex items-center gap-1"><FaSpinner className="animate-spin" /> Loading saved data…</span>
                          )}
                          <SaveButton
                            state={invoice.saveState ?? "idle"}
                            onClick={() => triggerSaveLatest(invoice.id, invoice)}
                          />
                        </div>
                      </div>

                      <div className="flex flex-col lg:flex-row gap-6 w-full">

                        {/* Left: user tabs */}
                        <div className="flex flex-col w-full sm:w-[320px] min-w-[180px] max-w-sm shrink-0 bg-gradient-to-br from-blue-100 to-blue-50 rounded-xl border-2 border-blue-100 shadow p-3 mt-4 mb-4 gap-2">
                          <div className="font-bold px-2 text-blue-800/80 text-xs flex items-center justify-between">
                            Settlement Users
                            <button
                              className={`text-xs flex gap-1 items-center px-2 py-0.5 rounded font-bold ${remaining<=0?"bg-gray-300 text-gray-500 cursor-not-allowed":"bg-blue-600 text-white hover:bg-black"}`}
                              onClick={()=>{ if(remaining>0) openEditDist(invoice, null); else alert("No remaining amount."); }}>
                              <FaPlus /> Add
                            </button>
                          </div>

                          {participants.length > 0 && (
                            <div className="px-1 pb-1">
                              <div className="text-[10px] font-bold text-blue-500 uppercase tracking-widest mb-1 flex items-center gap-1">
                                <FaUser className="text-[9px]" /> Invoice Participants
                              </div>
                              <div className="flex flex-col gap-1">
                                {participants.map((p) => {
                                  const alreadyAdded = invoice.userDistributions.some(
                                    (d) => d.userType === p.userType && d.userName === p.userName
                                  );
                                  return (
                                    <div key={`${p.userType}::${p.userName}`}
                                      className={`flex items-center justify-between rounded px-2 py-1 text-xs border ${alreadyAdded ? "bg-green-50 border-green-200 text-green-700" : "bg-white border-blue-100 text-blue-800"}`}>
                                      <span className="font-semibold truncate flex-1 min-w-0">
                                        <span className="text-blue-400 font-bold capitalize">{userTypeLabel[p.userType]||p.userType}:</span>{" "}
                                        {p.userName}
                                      </span>
                                      {alreadyAdded
                                        ? <span className="ml-2 flex items-center gap-0.5 text-green-600 font-bold shrink-0"><FaCheckCircle className="text-xs"/> Added</span>
                                        : (
                                          <button
                                            disabled={remaining <= 0}
                                            className="ml-2 px-2 py-0.5 rounded bg-blue-600 text-white font-bold hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                                            onClick={() => {
                                              setEditDist({
                                                invoiceId: invoice.id, editingIdx: null, source: "auto",
                                                selectedParticipant: `${p.userType}::${p.userName}`,
                                                userType: p.userType, userName: p.userName, amount: "",
                                              });
                                            }}>
                                            + Add
                                          </button>
                                        )
                                      }
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {invoice.userDistributions.length === 0 && (
                            <div className="text-center text-blue-400 text-xs italic py-2">No distributions yet.</div>
                          )}
                          <div className="flex flex-col gap-1">
                            {invoice.userDistributions.map((u,idx)=>{
                              const isActive = activeIdx===idx;
                              return (
                                <div key={`${u.userType}_${idx}`}
                                  className={`relative rounded-lg bg-white hover:bg-blue-100/70 border-2 ${isActive?"border-blue-700 ring-2 ring-blue-500/20 shadow-lg":"border-blue-100"} flex items-center px-2 pr-20 py-2 my-1 transition-all`}
                                  style={{minHeight:48}}>
                                  <div className="flex-1 flex flex-col min-w-0 cursor-pointer" onClick={()=>setSelectedUserTab((t)=>({...t,[invoice.id]:idx}))}>
                                    <button type="button" onClick={()=>setSelectedUserTab((t)=>({...t,[invoice.id]:idx}))} className="text-left focus:outline-none w-full">
                                      <div className="flex items-center gap-2">
                                        <span className="font-bold text-blue-900 text-sm capitalize truncate">
                                          {userTypeLabel[u.userType]||u.userType}
                                          {u.userName&&<span className="font-normal text-xs text-black/50 ml-1">: {u.userName}</span>}
                                        </span>
                                        <span className="ml-auto flex items-center gap-1">
                                          <span className={`rounded px-2 py-0.5 text-xs font-bold ${u.paid?"bg-green-100 text-green-700":"bg-yellow-100 text-yellow-800"}`}>
                                            ₹{u.amount?.toLocaleString("en-IN")}
                                          </span>
                                          {u.paid?<FaCheckCircle className="text-green-600 ml-1"/>:<FaTimesCircle className="text-black/30 ml-1"/>}
                                        </span>
                                      </div>
                                    </button>
                                    <div className="pl-1 mt-1 flex flex-wrap gap-2 text-xs text-black/70">
                                      <span>Allocated: <span className="text-blue-700 font-semibold">₹{u.amount?.toLocaleString("en-IN")}</span></span>
                                      <span>Paid: <span className="text-green-700 font-semibold">₹{sumPaid(u).toLocaleString("en-IN")}</span></span>
                                      <span>Remain: <span className="text-yellow-700 font-semibold">₹{Math.max(0,(u.amount??0)-sumPaid(u)).toLocaleString("en-IN")}</span></span>
                                    </div>
                                  </div>
                                  <div className="absolute top-1 right-4 flex flex-col gap-1">
                                    <button className="p-1 rounded text-gray-400 bg-gray-100 hover:bg-blue-600 hover:text-white border border-gray-300 transition" style={{display:"inline-flex",alignItems:"center",fontSize:"0.8rem"}} onClick={()=>openEditDist(invoice,idx,u)} title="Edit"><FaEdit/></button>
                                    <button className="p-1 rounded text-gray-500 bg-gray-200 hover:bg-red-500 hover:text-white border border-gray-300 transition" style={{display:"inline-flex",alignItems:"center",fontSize:"0.8rem"}} onClick={()=>removeDist(invoice.id,idx)} title="Remove"><FaTrash/></button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <div className="px-3 py-1 text-xs font-bold text-blue-900 mt-2 bg-blue-50 rounded text-center">
                            Allocated: ₹{totalAllocated.toLocaleString("en-IN")}<br/>
                            Remaining: <span className={remaining===0?"text-green-700":"text-yellow-800"}>₹{remaining.toLocaleString("en-IN")}</span>
                          </div>
                        </div>

                        {/* Right: detail panel */}
                        <div className="flex-1 min-w-0 bg-gradient-to-br from-blue-50 to-white rounded-xl border-2 border-blue-100 shadow px-2 sm:px-6 pt-3 pb-6 relative">
                          {activeUser ? (
                            <div className="mb-4 pb-4 border-b border-blue-100">
                              <div className="mb-2">
                                <span className="text-lg font-black text-blue-900">
                                  {userTypeLabel[activeUser.userType]||activeUser.userType}
                                  {activeUser.userName&&<span className="font-semibold text-black text-base">: {activeUser.userName}</span>}
                                </span>
                              </div>
                              <div className="flex flex-col sm:flex-row gap-2 w-full">
                                {[
                                  {label:"Payable",  val:activeUser.amount,                                           cls:"text-blue-800",  bg:"bg-blue-50"},
                                  {label:"Paid",     val:sumPaid(activeUser),                                         cls:"text-green-800", bg:"bg-green-50"},
                                  {label:"Remaining",val:Math.max(0,(activeUser.amount??0)-sumPaid(activeUser)),      cls:"text-yellow-800",bg:"bg-yellow-50"},
                                ].map((c)=>(
                                  <div key={c.label} className={`${c.bg} rounded-lg p-2 border flex-1`}>
                                    <div className="text-xs text-black/70 font-bold">{c.label}</div>
                                    <div className={`font-bold ${c.cls} text-lg`}>₹{c.val.toLocaleString("en-IN")}</div>
                                  </div>
                                ))}
                              </div>
                              <div className="mt-3 flex flex-wrap gap-3">
                                <button className="flex items-center gap-1 px-4 py-2 text-sm rounded-xl bg-green-600 text-white font-bold border-2 border-green-700 hover:bg-black hover:text-green-300 shadow disabled:opacity-40 disabled:cursor-not-allowed"
                                  disabled={sumPaid(activeUser)>=(activeUser.amount??0)}
                                  onClick={()=>openPay(invoice.id,activeIdx,Math.max(0,(activeUser.amount??0)-sumPaid(activeUser)),"bank",new Date().toISOString().slice(0,10),true)}>
                                  <FaMoneyCheckAlt/> Full Payment
                                </button>
                                <button className="flex items-center gap-1 px-4 py-2 text-sm rounded-xl bg-blue-600 text-white font-bold border-2 border-blue-700 hover:bg-black hover:text-blue-400 shadow disabled:opacity-40 disabled:cursor-not-allowed"
                                  disabled={sumPaid(activeUser)>=(activeUser.amount??0)}
                                  onClick={()=>openPay(invoice.id,activeIdx,0,"bank",new Date().toISOString().slice(0,10))}>
                                  <FaMoneyCheckAlt/> Partial Payment
                                </button>
                              </div>
                              <div className="mt-4">
                                <div className="text-blue-900 font-bold text-xs mb-1">Payment Records</div>
                                <div className="overflow-x-auto">
                                  <table className="w-full text-xs border bg-white rounded">
                                    <thead><tr className="bg-blue-50">
                                      <th className="p-1 border text-left">Date</th>
                                      <th className="p-1 border text-left">Amount</th>
                                      <th className="p-1 border text-left">Mode</th>
                                      <th className="p-1 border text-left">Action</th>
                                    </tr></thead>
                                    <tbody>
                                      {activeUser.paymentRecords.length===0&&<tr><td colSpan={4} className="p-2 text-center text-gray-400">No records yet</td></tr>}
                                      {activeUser.paymentRecords.map((rec,i)=>(
                                        <tr key={i}>
                                          <td className="p-1 border">{rec.date||"—"}</td>
                                          <td className="p-1 border">₹{rec.amount?.toLocaleString("en-IN")}</td>
                                          <td className="p-1 border">{paymentModeLabel(rec.paymentMode)}</td>
                                          <td className="p-1 border">
                                            <button className="mr-1 px-1 py-0.5 text-xs bg-blue-100 rounded hover:bg-blue-600 hover:text-white" onClick={()=>openPay(invoice.id,activeIdx,rec.amount,rec.paymentMode,rec.date,false,true,i)} title="Edit"><FaEdit/></button>
                                            <button className="px-1 py-0.5 text-xs bg-gray-200 rounded hover:bg-red-600 hover:text-white" onClick={()=>removePayRec(invoice.id,activeIdx,i)} title="Remove"><FaTrash/></button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="text-blue-400 text-sm italic py-6 text-center">No distribution added yet. Use "Add" on the left.</div>
                          )}

                          {/* Invoice info */}
                          <div className="mt-4 mb-3 flex flex-col gap-1.5 text-sm">
                            <span className="text-lg font-black text-black">{invoice.company}</span>
                            <div><b>Sender:</b> {invoice.sender}{invoice.senderAddress&&<span className="text-black/40"> ({invoice.senderAddress})</span>}</div>
                            <div><b>Receiver:</b> {invoice.receiver}{invoice.receiverAddress&&<span className="text-black/40"> ({invoice.receiverAddress})</span>}</div>
                            {invoice.broker && invoice.broker !== "—" && (
                              <div><b>Broker:</b> {invoice.broker}{invoice.brokerAddress&&<span className="text-black/40"> ({invoice.brokerAddress})</span>}</div>
                            )}
                            <div><b>Transporter:</b> {invoice.transporter} &nbsp;|&nbsp; <b>Driver:</b> {invoice.driver}</div>
                            {invoice.subjects&&<div><b>Subject:</b> {invoice.subjects}</div>}
                            <div className="flex gap-2 flex-wrap mt-2">
                              <span className="text-xs font-bold bg-blue-800 text-white px-2 py-1 rounded">Status: {invoice.paymentType}</span>
                              <span className="text-xs font-bold bg-green-600 text-white px-2 py-1 rounded">₹{invoice.payment.toLocaleString("en-IN")}</span>
                            </div>
                            {invoice.pdfLink&&(
                              <a href={invoice.pdfLink} target="_blank" rel="noopener noreferrer"
                                className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-blue-700 to-blue-500 text-white font-bold hover:from-black hover:to-black shadow transition w-fit text-sm">
                                <FaDownload/> Download PDF
                              </a>
                            )}
                            {(invoice.companyGst||invoice.companyPan)&&(
                              <div className="mt-2 text-xs text-blue-900/80 font-bold">
                                {invoice.companyGst&&<><b>GST:</b> {invoice.companyGst}&nbsp;|&nbsp;</>}
                                {invoice.companyPan&&<><b>PAN:</b> {invoice.companyPan}</>}
                                {invoice.companyEmail&&<><br/><b>Email:</b> {invoice.companyEmail}</>}
                              </div>
                            )}
                          </div>

                          {invoice.items.length>0&&(
                            <div className="border rounded-xl overflow-x-auto shadow bg-white/90">
                              <table className="min-w-[400px] w-full text-xs">
                                <thead className="bg-gradient-to-r from-blue-100 to-blue-200 border-b border-blue-200 text-blue-800">
                                  <tr>
                                    <th className="p-2">#</th><th className="p-2 text-left">Product</th>
                                    <th className="p-2">HSN</th><th className="p-2">UOM</th>
                                    <th className="p-2">Qty</th><th className="p-2">Rate</th><th className="p-2">Amount</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {invoice.items.map((it,i)=>(
                                    <tr key={it.id} className="border-b border-gray-100 text-black">
                                      <td className="p-2 text-center">{i+1}</td>
                                      <td className="p-2">{it.product}</td>
                                      <td className="p-2 text-center">{it.hsn}</td>
                                      <td className="p-2 text-center">{it.uom}</td>
                                      <td className="p-2 text-center">{it.qty||"—"}</td>
                                      <td className="p-2 text-center">{it.rate?`₹${it.rate}`:"—"}</td>
                                      <td className="p-2 font-bold text-blue-800 text-right">{it.amount?`₹${it.amount.toLocaleString("en-IN")}`:"—"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}

                          {/* Pay modal */}
                          {payModal&&payModal.invoiceId===invoice.id&&payModal.userIdx===activeIdx&&(
                            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
                              <div className="bg-white p-6 rounded-xl border-2 border-blue-600 shadow-2xl w-80 space-y-3">
                                <div className="text-black font-bold text-lg">
                                  {payModal.editing?"Edit Payment":payModal.full?"Full Payment":"Partial Payment"} for {userTypeLabel[invoice.userDistributions[payModal.userIdx]?.userType]??"User"}
                                </div>
                                <div>
                                  <label className="block text-sm font-bold mb-1">Amount</label>
                                  <input type="number" min={0} className="rounded px-2 py-1 border border-gray-300 text-black w-full"
                                    value={payModal.full?(invoice.userDistributions[payModal.userIdx].amount-sumPaid(invoice.userDistributions[payModal.userIdx])).toString():payModal.amt}
                                    readOnly={!!payModal.full}
                                    onChange={(e)=>setPayModal((p)=>p?{...p,amt:e.target.value.replace(/^0+/,"")}:p)} />
                                </div>
                                <div>
                                  <label className="block text-sm font-bold mb-1">Payment Mode</label>
                                  <select className="rounded px-2 py-1 border border-gray-300 text-black w-full" value={payModal.mode}
                                    onChange={(e)=>setPayModal((p)=>p?{...p,mode:e.target.value as PaymentMode}:p)}>
                                    <option value="bank">Bank</option><option value="online">Online</option>
                                    <option value="cash">Cash</option><option value="partial">Partial</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-sm font-bold mb-1">Date</label>
                                  <input type="date" className="rounded px-2 py-1 border border-gray-300 text-black w-full" value={payModal.date}
                                    onChange={(e)=>setPayModal((p)=>p?{...p,date:e.target.value}:p)} />
                                </div>
                                <div className="flex gap-2 justify-end">
                                  <button className="bg-blue-600 rounded text-white text-xs px-3 py-1 font-semibold hover:bg-black border-2 border-blue-600 disabled:opacity-40"
                                    disabled={!payModal.full&&Number(payModal.amt)<=0}
                                    onClick={()=>{
                                      const u=invoice.userDistributions[payModal.userIdx];
                                      const amt=payModal.full?u.amount-sumPaid(u):Number(payModal.amt);
                                      handlePayment(payModal.invoiceId,payModal.userIdx,amt,payModal.mode,payModal.date,payModal.editing?payModal.editingIdx:undefined);
                                    }}>
                                    {payModal.editing?"Save":"Record Payment"}
                                  </button>
                                  <button onClick={()=>setPayModal(null)} className="bg-black/80 text-white px-2 py-1 rounded text-xs hover:bg-blue-600 border border-black">Cancel</button>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Edit / Add Distribution Modal */}
                          {editDist&&editDist.invoiceId===invoice.id&&(()=>{
                            const maxAmt = editDist.editingIdx != null
                              ? invoice.payment - sumAllocated(invoice.userDistributions.filter((_,i)=>i!==editDist.editingIdx))
                              : invoice.payment - sumAllocated(invoice.userDistributions);
                            return (
                              <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
                                <div className="bg-white p-6 rounded-xl border-2 border-blue-600 shadow-2xl w-[340px] space-y-4">
                                  <div className="text-black font-bold text-lg">
                                    {editDist.editingIdx!=null?"Edit Settlement User":"Add Settlement User"}
                                  </div>
                                  <div className="flex gap-2 rounded-lg overflow-hidden border border-blue-200">
                                    <button className={`flex-1 py-1.5 text-xs font-bold transition ${editDist.source==="auto"?"bg-blue-600 text-white":"bg-white text-blue-700 hover:bg-blue-50"}`}
                                      onClick={()=>{ const first=participants[0]; setEditDist((p)=>p?{...p,source:"auto",selectedParticipant:first?`${first.userType}::${first.userName}`:"",userType:first?.userType||"",userName:first?.userName||""}:p); }}>
                                      From Invoice
                                    </button>
                                    <button className={`flex-1 py-1.5 text-xs font-bold transition ${editDist.source==="manual"?"bg-blue-600 text-white":"bg-white text-blue-700 hover:bg-blue-50"}`}
                                      onClick={()=>setEditDist((p)=>p?{...p,source:"manual",selectedParticipant:"",userType:"",userName:""}:p)}>
                                      Manual Entry
                                    </button>
                                  </div>
                                  {editDist.source==="auto" && (
                                    <div>
                                      <label className="block text-sm font-bold mb-1">Select Participant</label>
                                      {participants.length === 0 ? (
                                        <div className="text-xs text-gray-400 italic px-2 py-1">No participants found. Use Manual Entry.</div>
                                      ) : (
                                        <select className="rounded px-2 py-1 border border-gray-300 text-black w-full"
                                          value={editDist.selectedParticipant}
                                          onChange={(e)=>handleParticipantSelect(e.target.value, participants)}>
                                          <option value="">Select…</option>
                                          {participants.map((p)=>(
                                            <option key={`${p.userType}::${p.userName}`} value={`${p.userType}::${p.userName}`}>
                                              {userTypeLabel[p.userType]||p.userType}: {p.userName}
                                            </option>
                                          ))}
                                        </select>
                                      )}
                                      {editDist.userType && (
                                        <div className="mt-2 text-xs bg-blue-50 rounded px-2 py-1 text-blue-800 font-semibold border border-blue-100">
                                          <span className="capitalize">{userTypeLabel[editDist.userType]||editDist.userType}</span>: {editDist.userName}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  {editDist.source==="manual" && (
                                    <>
                                      <div>
                                        <label className="block text-sm font-bold mb-1">User Type</label>
                                        <select className="rounded px-2 py-1 border border-gray-300 text-black w-full" value={editDist.userType}
                                          onChange={(e)=>setEditDist((p)=>p?{...p,userType:e.target.value}:p)}>
                                          <option value="">Select…</option>
                                          {presetUserTypes.map((t)=><option key={t} value={t}>{userTypeLabel[t]||t}</option>)}
                                        </select>
                                      </div>
                                      <div>
                                        <label className="block text-sm font-bold mb-1">User Name</label>
                                        <input type="text" className="rounded px-2 py-1 border border-gray-300 text-black w-full"
                                          placeholder="Enter name…" value={editDist.userName}
                                          onChange={(e)=>setEditDist((p)=>p?{...p,userName:e.target.value}:p)} />
                                      </div>
                                    </>
                                  )}
                                  {(editDist.userType || editDist.source==="manual") && (
                                    <div>
                                      <label className="block text-sm font-bold mb-1">Payable Amount</label>
                                      <input type="number" min={1} max={maxAmt}
                                        className="rounded px-2 py-1 border border-gray-300 text-black w-full"
                                        placeholder="Enter amount…" value={editDist.amount}
                                        onChange={(e)=>setEditDist((p)=>p?{...p,amount:e.target.value.replace(/^0+/,"")}:p)} />
                                      <span className="text-xs text-blue-600 font-bold">/ Max ₹{maxAmt.toLocaleString("en-IN")}</span>
                                    </div>
                                  )}
                                  <div className="flex gap-2 justify-end pt-1">
                                    <button className="bg-blue-600 rounded text-white text-xs px-3 py-1 font-semibold hover:bg-black border-2 border-blue-600 disabled:opacity-40"
                                      disabled={!editDist.userType||Number(editDist.amount)<1}
                                      onClick={saveEditDist}>
                                      {editDist.editingIdx!=null?"Save":"Add"}
                                    </button>
                                    <button onClick={()=>setEditDist(null)} className="bg-black/80 text-white px-2 py-1 rounded text-xs hover:bg-blue-600 border border-black">Cancel</button>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Settlement;